import { funderReviewPath } from "../useFunderWorkspaceRouteState";
import {
  FUNDER_WORKSPACE_VIDEO_TARGET,
  setFunderWorkspaceLandingTarget,
} from "@client/utils/funderWorkspaceLanding";
import { isRailOnlyVideoKind, isRoundLevelVideoKind } from "@lib/videoKinds";

/**
 * The workspace URL that shows a given video, with it selected and the page
 * landing on the player.
 *
 * `videoType` is REQUIRED, deliberately: which rail shows a video is a property
 * of its KIND, not of whether it has a track. Every new video carries a track
 * (`finalizeVideoUpload` requires one), so a round overview has one too —
 * building its URL from that track yields a track page where `getMeetings`
 * never returns it, and the `?video=` target then silently fails to resolve.
 * A required argument is what forces each call site to answer the question
 * rather than defaulting to the wrong answer.
 */
export function meetingDeepLink(
  roundId: string,
  videoId: string,
  trackId: string | null,
  videoType: string | null,
  // For a rail-only kind, the recommender whose page is this video's canonical
  // home (resolved server-side in getVideos — the client can't work it out,
  // since it lives in tag rows rather than on the video). Null means no
  // addressable home; the caller gets the bare rail rather than a link whose
  // target can't resolve.
  homeRecommenderId?: string | null,
): string {
  const params = new URLSearchParams({ video: videoId });
  setFunderWorkspaceLandingTarget(params, FUNDER_WORKSPACE_VIDEO_TARGET);

  // Round-level kinds live on the Overview rail, which takes no rail item id.
  if (videoType != null && isRoundLevelVideoKind(videoType)) {
    return `${funderReviewPath(roundId, "overview")}?${params}`;
  }

  // A rail-only kind (a recommender rollup) has no strip URL at all: it renders
  // inside one person's clips strip, and WHICH person comes from the video's
  // tags, which the stored queue doesn't carry. Falling through to the track URL
  // below would mint a `?video=` target that getMeetings never returns, so the
  // link would land somewhere plausible and silently select the wrong video.
  // Land on the recommenders rail with no target instead — honest about not
  // knowing the destination. Properly addressing these needs rail context
  // stored alongside the queue entry; see the note on the queue controls.
  if (videoType != null && isRailOnlyVideoKind(videoType)) {
    if (homeRecommenderId == null) return funderReviewPath(roundId, "recs");
    // The rail selects a video by `?excerpt=`, not `?video=` — matching the
    // Watch pill, which is the established way into this strip.
    const railParams = new URLSearchParams({ excerpt: videoId });
    setFunderWorkspaceLandingTarget(railParams, FUNDER_WORKSPACE_VIDEO_TARGET);
    return `${funderReviewPath(roundId, "recs", homeRecommenderId)}?${railParams}`;
  }

  // A track-scoped video with no track can't be addressed — the tracks rail has
  // no item to open — so fall back to the bare rail rather than minting a URL
  // whose target can never resolve.
  if (!trackId) return funderReviewPath(roundId, "tracks");

  return `${funderReviewPath(roundId, "tracks", trackId)}?${params}`;
}
